import { FC, useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { LinkButton, Modal } from 'src/components';
import { ROUTES } from 'src/constants/routes.const';
import { useUserContext } from 'src/context/user';
import { TrackingApi } from 'src/api';
import { MilestoneUpsellTrackingAction } from 'src/api/tracking/types';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { MilestoneUpsellVariant } from './getMilestoneUpsellVariant';
import {
  getMilestoneSource,
  markMilestoneUpsellShown,
  MilestoneType,
} from './milestoneUpsellStorage';
import { useMilestoneUpsellVariant } from './useMilestoneUpsellVariant';
import * as S from './styles';

const msgs = defineMessages({
  streakTitle: {
    id: 'milestone_upsell_streak_title',
    defaultMessage: "You're not 'just curious' anymore",
  },
  streakBody: {
    id: 'milestone_upsell_streak_body',
    defaultMessage:
      "Seven days straight. That's 'I'm all in' territory. Pro keeps every door open.",
  },
  streakCta: {
    id: 'milestone_upsell_streak_cta',
    defaultMessage: 'Go all in. See Pro plans',
  },
  streakSkip: {
    id: 'milestone_upsell_streak_skip',
    defaultMessage: 'Keep learning free',
  },
  moduleTitle: {
    id: 'milestone_upsell_module_title',
    defaultMessage: 'Wordlist complete!',
  },
  moduleBody: {
    id: 'milestone_upsell_module_body',
    defaultMessage:
      'All {count} words in "{title}" learnt. Ready to get serious? Pro unlocks every scenario.',
  },
  moduleCta: {
    id: 'milestone_upsell_module_cta',
    defaultMessage: 'Unlock everything with Pro',
  },
  moduleSkip: {
    id: 'milestone_upsell_module_skip',
    defaultMessage: 'Continue for free',
  },
  commitmentTitleStreak: {
    id: 'milestone_upsell_commitment_title_streak',
    defaultMessage: 'A 7-day streak says you meant it',
  },
  commitmentTitleModule: {
    id: 'milestone_upsell_commitment_title_module',
    defaultMessage: 'Finishing a wordlist says you meant it',
  },
  commitmentBody: {
    id: 'milestone_upsell_commitment_body',
    defaultMessage: 'Make it official.',
  },
  commitmentCta: {
    id: 'milestone_upsell_commitment_cta',
    defaultMessage: 'See Pro plans',
  },
  commitmentSkip: {
    id: 'milestone_upsell_commitment_skip',
    defaultMessage: 'Not now',
  },
  commitmentCurious: {
    id: 'milestone_upsell_commitment_curious',
    defaultMessage: 'Just curious',
  },
  commitmentSerious: {
    id: 'milestone_upsell_commitment_serious',
    defaultMessage: 'Ready to get serious?',
  },
  commitmentAllIn: {
    id: 'milestone_upsell_commitment_all_in',
    defaultMessage: "I'm all in",
  },
});

export interface MilestoneUpsellProps {
  milestone: MilestoneType;
  onContinue: () => void;
  /** Override the MEME experiment arm, primarily for previews and tests. */
  variant?: MilestoneUpsellVariant;
  wordlistTitle?: string;
  wordCount?: number;
  /** Extra query params carried into the plans page the CTA links to. */
  plansQuery?: Record<string, string>;
}

type ResolvedMilestoneUpsellProps = Omit<MilestoneUpsellProps, 'variant'> & {
  variant: MilestoneUpsellVariant;
};

const ResolvedMilestoneUpsell: FC<ResolvedMilestoneUpsellProps> = ({
  milestone,
  onContinue,
  variant,
  wordlistTitle,
  wordCount,
  plansQuery,
}) => {
  const intl = useIntl();
  const { user } = useUserContext();
  const languagePairId = getLanguagePairId();
  const source = getMilestoneSource(milestone);

  const trackAction = useCallback(
    (action: MilestoneUpsellTrackingAction) => {
      void TrackingApi.trackUserAction(action, 'milestone_upsell', languagePairId, !!user?.isPro, {
        milestone,
        source,
        variant,
      });
    },
    [languagePairId, user?.isPro, milestone, source, variant],
  );

  useEffect(() => {
    markMilestoneUpsellShown(user, milestone);
    trackAction('screen_viewed');
    // Fire once on mount for this surface
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubscribeClick = useCallback(() => {
    trackAction('clicked_subscribe');
  }, [trackAction]);

  const handleDismiss = useCallback(() => {
    trackAction('paywall_dismissed');
    onContinue();
  }, [trackAction, onContinue]);

  const content = (() => {
    if (variant === 'C') {
      return {
        title: intl.formatMessage(
          milestone === 'streak_7' ? msgs.commitmentTitleStreak : msgs.commitmentTitleModule,
        ),
        body: intl.formatMessage(msgs.commitmentBody),
        cta: intl.formatMessage(msgs.commitmentCta),
        skip: intl.formatMessage(msgs.commitmentSkip),
        showCommitments: true as const,
        icon: null,
      };
    }

    if (variant === 'B' || milestone === 'module_complete') {
      return {
        title: intl.formatMessage(msgs.moduleTitle),
        body: intl.formatMessage(msgs.moduleBody, {
          count: wordCount ?? 0,
          title: wordlistTitle ?? '',
        }),
        cta: intl.formatMessage(msgs.moduleCta),
        skip: intl.formatMessage(msgs.moduleSkip),
        showCommitments: false as const,
        icon: 'tickFilled' as const,
      };
    }

    return {
      title: intl.formatMessage(msgs.streakTitle),
      body: intl.formatMessage(msgs.streakBody),
      cta: intl.formatMessage(msgs.streakCta),
      skip: intl.formatMessage(msgs.streakSkip),
      showCommitments: false as const,
      icon: 'fire' as const,
    };
  })();

  return (
    <Modal isOpen fullSize hideCloseButton handleClose={handleDismiss}>
      <S.ContentOuterContainer data-testid="milestone-upsell">
        <S.ContentInnerContainer>
          {content.icon && (
            <S.IconBadge aria-hidden>
              <S.BadgeIcon name={content.icon} />
              {content.icon === 'fire' && <S.StreakCount>7</S.StreakCount>}
            </S.IconBadge>
          )}
          <S.Title>{content.title}</S.Title>
          {content.showCommitments && (
            <S.CommitmentList aria-label={content.title}>
              <S.CommitmentOption>{intl.formatMessage(msgs.commitmentCurious)}</S.CommitmentOption>
              <S.CommitmentOption>{intl.formatMessage(msgs.commitmentSerious)}</S.CommitmentOption>
              <S.CommitmentOption $active>
                {intl.formatMessage(msgs.commitmentAllIn)}
                <S.CommitmentCheck name="check" />
              </S.CommitmentOption>
            </S.CommitmentList>
          )}
          <S.Description>{content.body}</S.Description>
          <S.ButtonContainer>
            <LinkButton
              buttonType="primary"
              buttonSize="large"
              wide
              href={{ pathname: ROUTES.payment.plans, query: { ...plansQuery, source } }}
              onClick={handleSubscribeClick}
            >
              {content.cta}
            </LinkButton>
            <S.FreeLink type="button" onClick={handleDismiss} data-testid="milestone-upsell-skip">
              {content.skip}
            </S.FreeLink>
          </S.ButtonContainer>
        </S.ContentInnerContainer>
      </S.ContentOuterContainer>
    </Modal>
  );
};

export const MilestoneUpsell: FC<MilestoneUpsellProps> = props => {
  const { user } = useUserContext();
  const variant = useMilestoneUpsellVariant(user?.id, props.milestone, props.variant);

  if (!variant) return null;

  return <ResolvedMilestoneUpsell {...props} variant={variant} />;
};
